// Lib
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import {
    ScreenOrientation,
    OrientationType as CapacitorOrientationType,
} from '@capawesome/capacitor-screen-orientation';
import { CAPACITOR_SCREEN_ORIENTATION_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import { getPlatformDetailsSelector, selectIsMobileAppMode } from '../platformSelector';
import { isPlatformNativeMobileApp } from '../utils/platformDetailsUtils';
import { PlatformDetails } from '../../../common/platform/platformTypes';

let lockCount = 0;

/**
 * Whether a requested portrait lock should actually be imposed.
 * The lock protects the mobile workspace's portrait-only screens, so it only applies while that workspace
 * is showing. The desktop workspace lays out in any orientation, and on a resizable device the platform
 * may ignore the lock anyway. The lock and unlock methods are also only available in the native apps.
 */
export const shouldLockScreenOrientationPortrait = (
    lockScreenOrientation: boolean,
    isMobileAppMode: boolean,
    platformDetails: PlatformDetails,
): boolean => lockScreenOrientation && isMobileAppMode && isPlatformNativeMobileApp(platformDetails);

/**
 * Locks the screen orientation to portrait mode when `lockScreenOrientation` is true and the
 * mobile workspace is showing.
 */
export default function useLockScreenOrientationPortrait(lockScreenOrientation: boolean) {
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const isMobileAppMode = useSelector(selectIsMobileAppMode);

    const shouldLock = shouldLockScreenOrientationPortrait(lockScreenOrientation, isMobileAppMode, platformDetails);

    useEffect(() => {
        if (!Capacitor.isPluginAvailable(CAPACITOR_SCREEN_ORIENTATION_PLUGIN)) return;

        if (!shouldLock) return;

        lockCount++;

        // NOTE: These are promise based, but we don't need to await them
        ScreenOrientation.lock({ type: CapacitorOrientationType.PORTRAIT });

        return () => {
            lockCount--;
            if (lockCount > 0) return;

            ScreenOrientation.unlock();
        };
    }, [shouldLock]);
}
